class ComplementaryController {
  constructor() {
    this.init();
  }

  get variantId() {
    //@ts-ignore
    return window.complementary_product;
  }

  // Once a shopper removes the free wash-bag we must not silently re-add it
  // (that's the "gift keeps coming back / not removable" bug). The dismissal is
  // remembered for the session and cleared once the cart no longer holds any
  // qualifying product, so a brand-new shop re-offers the gift.
  get dismissed() {
    try {
      return sessionStorage.getItem("complementaryDismissed") === "1";
    } catch (e) {
      return false;
    }
  }

  set dismissed(value) {
    try {
      if (value) sessionStorage.setItem("complementaryDismissed", "1");
      else sessionStorage.removeItem("complementaryDismissed");
    } catch (e) {
      /* sessionStorage unavailable (private mode) — fall back to no memory */
    }
  }

  async addProduct(cart = null, { silent = false } = {}) {
    if (!this.variantId) return;
    if (!cart) {
      cart = await fetch("/cart.js").then((res) => res.json());
    }

    const complementaryItem = cart.items.find(
      (item) => item.variant_id === this.variantId,
    );

    // "Qualifying" items are everything except the gift itself and any product
    // the merchant has excluded (e.g. scrunchies, yoga bag). A cart of only
    // excluded accessories must NOT trigger the free wash-bag.
    const excludedHandles = window.complementary_exclude_handles || [];
    const qualifyingItems = cart.items.filter(
      (item) =>
        item.variant_id !== this.variantId &&
        !excludedHandles.includes(item.handle),
    );

    // No qualifying products: the gift must NEVER sit in the cart alone. Remove
    // it if present, and clear any dismissal so a fresh shop re-offers it.
    if (qualifyingItems.length === 0) {
      this.dismissed = false;
      if (complementaryItem) {
        await this.removeProduct(cart);
      }
      return;
    }

    // Gift already in cart alongside qualifying items — nothing to do.
    if (complementaryItem) {
      return;
    }

    // Gift not in cart, qualifying items exist. Honour a prior user removal:
    // don't re-add what the shopper deliberately took out.
    if (this.dismissed) {
      return;
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: this.variantId,
        quantity: 1,
        properties: {
          _complementary: true,
        },
      }),
    });
    if (!response.ok) {
      console.error("Failed to add complementary product to cart");
      return;
    }

    const parsedState = await response.json();
    // `silent` lets a caller that will render the cart itself (e.g. the upsell
    // add handler) settle the wash-bag WITHOUT triggering a second, competing
    // re-render via the cartUpdate subscribers.
    if (silent) return;
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "complementary-product",
      cartData: parsedState,
      variantId: this.variantId,
    });
  }

  async removeProduct(cart = null) {
    if (!this.variantId) return;
    if (!cart) {
      cart = await fetch("/cart.js").then((res) => res.json());
    }
    const itemIndex = cart.items.findIndex(
      (item) => item.variant_id === this.variantId,
    );
    if (itemIndex === -1) {
      return;
    }

    // Line numbers are 1-based
    const line = itemIndex + 1;

    // Find the cart items element (drawer or page)
    const cartItemsElement =
      document.querySelector("cart-drawer-items") ||
      document.querySelector("cart-items");

    if (cartItemsElement && typeof cartItemsElement.updateQuantity === "function") {
      // Use the existing updateQuantity method which handles all UI updates
      cartItemsElement.updateQuantity(line, 0);
      return;
    }

    // Fallback: no cart element on this page (or its JS failed to load) —
    // remove via the Cart API directly so the wash-bag can never be stranded
    // as the only item in the cart.
    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: this.variantId, quantity: 0 }),
      });
      if (response.ok) {
        const parsedState = await response.json();
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "complementary-product",
          cartData: parsedState,
          variantId: this.variantId,
        });
      }
    } catch (e) {
      console.error("Failed to remove complementary product", e);
    }
  }

  init() {
    // Listen for custom events
    document.addEventListener("addComplementaryProduct", () =>
      this.addProduct(),
    );
    document.addEventListener("removeComplementaryProduct", () =>
      this.removeProduct(),
    );

    // Subscribe to cart updates, but ignore updates from this controller
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === "complementary-product") return;
      // If the shopper removed the gift itself, remember that and do NOT
      // re-add it. (The remove button reports the removed variant id.)
      if (
        event.variantId != null &&
        this.variantId != null &&
        Number(event.variantId) === Number(this.variantId)
      ) {
        this.dismissed = true;
        return;
      }
      // Always fetch fresh cart state so we never work from stale/partial data
      this.addProduct();
    });

    // Add complementary product on page load
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.addProduct());
    } else {
      this.addProduct();
    }
  }
}

// Create and attach instance to window
//@ts-ignore
window.complementaryController = new ComplementaryController();
